import React, { useState, useMemo } from 'react';
import { dynastyTokens } from '../../../services/colorService';
import PlayerLink from '../../../components/PlayerLink';
import OptimizerTradeDetail from './OptimizerTradeDetail';
import { acceptColor, positionLabel } from './optimizerUtils';

const colors = dynastyTokens.colors;

/* ── Utilities ── */
const ordSuffix = (n) => { if (!n) return '—'; const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };

const recTypeColor = (t) => {
  if (t === 'best_overall') return colors.primary;
  if (t === 'safest') return colors.success;
  if (t === 'highest_upside') return colors.dataPurple;
  if (t === 'best_category_fix') return colors.warning;
  if (t === 'best_dynasty') return colors.info;
  return colors.info;
};

const recTypeLabel = (t) => {
  if (t === 'best_overall') return 'BEST OVERALL';
  if (t === 'safest') return 'SAFEST';
  if (t === 'highest_upside') return 'HIGHEST UPSIDE';
  if (t === 'best_category_fix') return 'CATEGORY FIX';
  if (t === 'best_dynasty') return 'DYNASTY PLAY';
  return t?.toUpperCase() || '';
};

// T3-21: acceptColor is single-sourced in optimizerUtils (was 0.30/0.20 here — inconsistent w/ Flyout/Detail)

/* ── Section header ── */
const SectionLabel = ({ label, right }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: colors.primary, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 3, height: 12, background: colors.primary, borderRadius: 2 }} />
    {label}
    {right && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 500, color: colors.neutral[500], textTransform: 'none', letterSpacing: 0 }}>{right}</span>}
  </div>
);

/* ── Category pill ── */
const CatPill = ({ cat, color: pillColor }) => (
  <span style={{
    display: 'inline-block', padding: '1px 5px', borderRadius: 3, fontSize: 7, fontWeight: 700,
    background: `${pillColor || colors.success}18`, color: pillColor || colors.success,
    textTransform: 'uppercase', letterSpacing: 0.5,
  }}>{cat}</span>
);

/* ── Toggle button ── */
const ToggleBtn = ({ label, active, onClick, activeColor }) => {
  const ac = activeColor || colors.primary;
  return (
    <button
      onClick={onClick}
      style={{
        padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer',
        border: `1px solid ${active ? ac : colors.neutral[700]}`,
        background: active ? `${ac}20` : 'transparent',
        color: active ? ac : colors.neutral[400],
        transition: 'all 0.15s',
      }}
    >{label}</button>
  );
};

/* ── Recommendation Card ── */
const RecCard = ({ rec, teams: teamsList }) => {
  const tc = recTypeColor(rec.recommendation_type);
  const seq = rec.sequence || {};
  const pkg = seq.trade_package || seq.trade || {};
  // DD-RO-MARGINAL-0726 item 2: headline the ROS-PROJECTED STANDINGS GAIN, not net_value (an asset-value metric).
  const tradeGain = seq.total_standings_gain ?? rec.total_standings_gain ?? seq.trade_standings_gain ?? rec.trade_standings_gain ?? 0;
  const assetValue = pkg.net_value;  // item 3: secondary 'Asset value' line, never labeled gain
  const followupGain = seq.followup_standings_gain ?? 0;
  const accept = pkg.acceptance_probability || rec.acceptance_probability || 0;
  const cats = pkg.fills_needs?.categories || rec.fills_categories || [];

  return (
    <div style={{
      flex: '1 1 0', minWidth: 180, background: colors.neutral[850], borderRadius: 6,
      borderTop: `2px solid ${tc}`, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: tc, textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {recTypeLabel(rec.recommendation_type)}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: tradeGain > 0 ? colors.success : colors.neutral[500] }}>
          {tradeGain >= 0 ? '+' : ''}{tradeGain}
          {followupGain > 0 && <span style={{ fontSize: 10, color: colors.neutral[400] }}> +{followupGain}</span>}
          {/* DD-RO-TRADE-VALUATION-0729 Part 1: net_value is net marginal standings, NOT asset value — relabel */}
          {assetValue != null && <span style={{ fontSize: 9, color: colors.neutral[500], fontWeight: 400 }}> · net std {assetValue >= 0 ? '+' : ''}{assetValue}</span>}
        </span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: colors.neutral[100], lineHeight: 1.3 }}>
        {rec.headline || '—'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto' }}>
        <span style={{ fontSize: 10, color: acceptColor(accept) }}>{Math.round(accept * 100)}% likely</span>
        {cats.length > 0 && (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {cats.map(c => <CatPill key={c} cat={c} />)}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Sequence Row ── */
const SequenceRow = ({ seq, rank, leagueId, teams: teamsList, onOpenDetail }) => {
  const [expanded, setExpanded] = useState(false);
  const trade = seq.trade_package || seq.trade || {};
  const sends = trade.user_sends || {};
  const receives = trade.user_receives || {};
  const sendPlayers = sends.players || [];
  const recvPlayers = receives.players || [];
  const sendPicks = sends.picks || [];
  const recvPicks = receives.picks || [];
  const followUps = seq.follow_up_moves || [];
  // DD-RO-MARGINAL-0726 item 2: standings gain is the headline; net_value (item 3) is a secondary asset-value line.
  const gain = seq.total_standings_gain ?? (seq.trade_standings_gain || 0);
  const assetValue = trade.net_value;
  const accept = trade.acceptance_probability || 0;
  const tradeType = trade.trade_type || 'two_team';
  const hasPicks = sendPicks.length > 0 || recvPicks.length > 0;
  const fillsCats = trade.fills_needs?.categories || [];
  const fillsPos = trade.fills_needs?.positions || [];

  // Build summary line (handles pick-only trades)
  const sendParts = [
    ...sendPlayers.map(p => p.player_name || '?'),
    ...sendPicks.map(p => `Rd ${p.round || '?'} pick`),
  ];
  const recvParts = [
    ...recvPlayers.map(p => p.player_name || '?'),
    ...recvPicks.map(p => `Rd ${p.round || '?'} pick`),
  ];
  // DD-RO-SEQBUGS-FIX (desktop three-team parity): a cyclic deal read as "vs X · 1 follow-up"
  // because the summary showed only the user's leg, hiding the middle. Surface ALL THREE legs
  // — the player chain (Buxton → Steer → Holmes) and the team cycle (You → A → B → You) — so a
  // three-team deal is legible as one cyclic trade (mobile Frame A already does this).
  const isThree = (trade.trade_type || 'two_team') === 'three_team';
  const legs = trade.legs || [];
  const legPlayerChain = legs.map(l => (l.players || [])[0]?.player_name || '?').join(' → ');
  const teamCycle = legs.length === 3
    ? `You → ${legs.map(l => l.to_is_user ? 'You' : (l.to_team_name || '?')).join(' → ')}`
    : '';
  const summaryText = isThree && legPlayerChain
    ? legPlayerChain
    : `${sendParts.join(' + ') || 'picks'} → ${recvParts.join(' + ') || 'picks'}`;

  // Opponent team
  const oppTeamId = (trade.teams_involved || []).find(t => t !== trade.user_team_id);
  const teamName = (tid) => {
    const team = teamsList?.find(t => t.team_id === tid);
    return team?.team_name || 'Unknown';
  };

  return (
    <div style={{ marginBottom: 4 }}>
      {/* Collapsed row */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer',
          background: colors.neutral[850], borderRadius: expanded ? '6px 6px 0 0' : 6,
          border: `1px solid ${colors.neutral[700]}`, borderBottom: expanded ? 'none' : `1px solid ${colors.neutral[700]}`,
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = colors.neutral[800]; }}
        onMouseLeave={e => { e.currentTarget.style.background = colors.neutral[850]; }}
      >
        {/* Rank badge */}
        <div style={{
          width: 24, height: 24, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${colors.primary}20`, color: colors.primary, fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>{rank}</div>

        {/* Trade summary */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: colors.neutral[100], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {summaryText}
            </span>
            {tradeType === 'three_team' && (
              <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: `${colors.dataPurple}18`, color: colors.dataPurple, flexShrink: 0 }}>3-TEAM</span>
            )}
            {hasPicks && (
              <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: `${colors.info}18`, color: colors.info, flexShrink: 0 }}>+PICK</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <span style={{ fontSize: 10, color: colors.neutral[500] }}>
              {isThree ? (teamCycle || 'three-team cycle') : `vs ${teamName(oppTeamId)}`}{followUps.length > 0 ? ` · ${followUps.length} follow-up${followUps.length > 1 ? 's' : ''}` : ''}
            </span>
            {fillsCats.map(c => <CatPill key={c} cat={c} />)}
          </div>
        </div>

        {/* Metrics */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: gain > 0 ? colors.success : colors.neutral[500] }}>
            {gain > 0 ? '+' : ''}{gain} pts
            {/* item 3: secondary asset-value line (net_value), never labeled gain */}
            {assetValue != null && <span style={{ fontSize: 9, color: colors.neutral[500], fontWeight: 400 }}> · asset {assetValue >= 0 ? '+' : ''}{assetValue}</span>}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: acceptColor(accept) }}>
            {Math.round(accept * 100)}%
          </span>
          <span style={{ fontSize: 10, color: colors.neutral[500], transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          background: colors.neutral[850], border: `1px solid ${colors.neutral[700]}`, borderTop: 'none',
          borderRadius: '0 0 6px 6px', padding: '10px 12px',
        }}>
          {/* YOU SEND / YOU RECEIVE columns */}
          <div style={{ display: 'flex', gap: 16 }}>
            {/* YOU SEND */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.error, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>You Send</div>
              {sendPlayers.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <PlayerLink mlbId={p.player_id} name={p.player_name} leagueId={leagueId} style={{ fontSize: 12, fontWeight: 700, color: colors.neutral[100] }} />
                  <span style={{ fontSize: 9, color: colors.neutral[500] }}>{positionLabel(p)}</span>
                  {p.contract_salary != null && <span style={{ fontSize: 9, color: colors.neutral[500] }}>${p.contract_salary}M</span>}
                </div>
              ))}
              {sendPicks.map((pk, i) => (
                <div key={`pk-${i}`} style={{ fontSize: 10, color: colors.neutral[400], marginBottom: 3 }}>
                  Rd {pk.round} pick ({pk.year})
                </div>
              ))}
              {sends.cash > 0 && <div style={{ fontSize: 10, color: colors.neutral[400] }}>${sends.cash}M cash</div>}
              <div style={{ fontSize: 10, color: colors.neutral[600], marginTop: 4 }}>
                Value: {sends.total_value?.toFixed(1) || '—'}
              </div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, background: colors.neutral[700], alignSelf: 'stretch' }} />

            {/* YOU RECEIVE */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.success, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>You Receive</div>
              {recvPlayers.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <PlayerLink mlbId={p.player_id} name={p.player_name} leagueId={leagueId} style={{ fontSize: 12, fontWeight: 700, color: colors.neutral[100] }} />
                  <span style={{ fontSize: 9, color: colors.neutral[500] }}>{positionLabel(p)}</span>
                  {p.contract_salary != null && <span style={{ fontSize: 9, color: colors.neutral[500] }}>${p.contract_salary}M</span>}
                </div>
              ))}
              {recvPicks.map((pk, i) => (
                <div key={`pk-${i}`} style={{ fontSize: 10, color: colors.neutral[400], marginBottom: 3 }}>
                  Rd {pk.round} pick ({pk.year})
                </div>
              ))}
              {receives.cash > 0 && <div style={{ fontSize: 10, color: colors.neutral[400] }}>${receives.cash}M cash</div>}
              <div style={{ fontSize: 10, color: colors.neutral[600], marginTop: 4 }}>
                Value: {receives.total_value?.toFixed(1) || '—'}
              </div>
            </div>
          </div>

          {/* Follow-up moves. DD-RO-CLASSAUDIT T1-5 (I4): required repairs (addresses_trade_holes) are
              part of the gain shown; opportunistic moves are separated as optional add-ons so nothing the
              user could sum contradicts the headline. */}
          {followUps.length > 0 && (() => {
            const renderMove = (m, i) => {
              const pickup = m.player_in || {};
              const drop = m.player_out;
              const delta = m.standings_delta ?? m.points_delta;
              return (
                <div key={i} style={{ marginBottom: 4 }}>
                  {pickup.player_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: `${colors.success}18`, color: colors.success }}>PICKUP</span>
                      <PlayerLink mlbId={pickup.player_id} name={pickup.player_name} leagueId={leagueId} style={{ fontSize: 11, color: colors.neutral[200] }} />
                      <span style={{ fontSize: 9, color: colors.neutral[500] }}>{positionLabel(pickup)}</span>
                      {delta != null && delta !== 0 && (
                        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: delta > 0 ? colors.success : colors.neutral[500] }}>
                          {delta > 0 ? '+' : ''}{Number(delta).toFixed(1)}
                        </span>
                      )}
                    </div>
                  )}
                  {drop && drop.player_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: `${colors.error}18`, color: colors.error }}>DROP</span>
                      <PlayerLink mlbId={drop.player_id} name={drop.player_name} leagueId={leagueId} style={{ fontSize: 11, color: colors.neutral[400] }} />
                      <span style={{ fontSize: 9, color: colors.neutral[500] }}>{positionLabel(drop)}</span>
                    </div>
                  )}
                </div>
              );
            };
            const required = followUps.filter(m => m.addresses_trade_holes);
            const optional = followUps.filter(m => !m.addresses_trade_holes);
            return (
              <>
                {required.length > 0 && (
                  <>
                    <div style={{ height: 1, background: colors.neutral[700], margin: '10px 0' }} />
                    <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[400], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Follow-Up Moves</div>
                    {required.map(renderMove)}
                  </>
                )}
                {optional.length > 0 && (
                  <>
                    <div style={{ height: 1, background: colors.neutral[700], margin: '10px 0' }} />
                    <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[500], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Optional Add-Ons <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: colors.neutral[600] }}>— not included in the gain above</span></div>
                    {optional.map(renderMove)}
                  </>
                )}
              </>
            );
          })()}

          {/* Category fills */}
          {(fillsCats.length > 0 || fillsPos.length > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 9, fontWeight: 600, color: colors.neutral[500] }}>Fills:</span>
              {fillsCats.map(c => <CatPill key={c} cat={c} />)}
              {fillsPos.map(p => <CatPill key={p} cat={p} color={colors.info} />)}
            </div>
          )}

          {/* Full Analysis button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.neutral[700]}` }}>
            <button
              onClick={(e) => { e.stopPropagation(); onOpenDetail(seq); }}
              style={{
                padding: '5px 14px', fontSize: 11, fontWeight: 600,
                border: `1px solid ${colors.primary}`, borderRadius: 4,
                background: `${colors.primary}15`, color: colors.primary,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${colors.primary}30`; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${colors.primary}15`; }}
            >
              Full Analysis →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const OptimizerTradeRecs = ({ optimizerData, league, userTeam, teams, initialCatFilter }) => {
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortMode, setSortMode] = useState('pts');
  // DD-RO-BUGS2-0724 Bug 4: an Action Priority tile drill-down seeds the category filter on
  // mount (this component mounts fresh when the Sequences subview opens). 'all' otherwise.
  const [catFilter, setCatFilter] = useState(initialCatFilter || 'all');
  const [detailSequence, setDetailSequence] = useState(null);

  const tradeEngine = optimizerData?.trade_engine;
  const sequences = (tradeEngine?.trade_sequences || []).filter(s =>
    s.total_standings_gain > 0 || s.trade_standings_gain > 0
  );
  const recommendations = tradeEngine?.recommendations || [];
  const leagueId = league?.league_id;

  // Show all league scoring categories (not just those in sequences)
  const allCategories = useMemo(() => {
    const hitting = optimizerData?.meta?.categories?.hitting || [];
    const pitching = optimizerData?.meta?.categories?.pitching || [];
    return [...hitting, ...pitching];
  }, [optimizerData]);

  // Track which categories actually have matching sequences
  const activeCats = useMemo(() => {
    const cats = new Set();
    for (const seq of sequences) {
      const trade = seq.trade_package || seq.trade || {};
      for (const c of (trade.fills_needs?.categories || [])) {
        cats.add(c);
      }
    }
    return cats;
  }, [sequences]);

  // Filter sequences
  const filtered = useMemo(() => {
    let result = [...sequences];

    if (typeFilter === '2team') {
      result = result.filter(s => (s.trade_package || s.trade || {}).trade_type === 'two_team');
    } else if (typeFilter === '3team') {
      result = result.filter(s => (s.trade_package || s.trade || {}).trade_type === 'three_team');
    } else if (typeFilter === 'pick') {
      result = result.filter(s => {
        const t = s.trade_package || s.trade || {};
        return (t.user_sends?.picks?.length || 0) > 0 || (t.user_receives?.picks?.length || 0) > 0;
      });
    }

    if (catFilter !== 'all') {
      result = result.filter(s => {
        const t = s.trade_package || s.trade || {};
        return (t.fills_needs?.categories || []).includes(catFilter);
      });
    }

    // DD-RO-MARGINAL-0726 item 2: "points" sorts by the ROS-PROJECTED STANDINGS GAIN — the same number the
    // card headlines. net_value (asset value) is no longer the sort key for a standings-impact product.
    const displayGain = (s) => (s.total_standings_gain ?? (s.trade_standings_gain || 0));
    if (sortMode === 'pts') {
      result.sort((a, b) => displayGain(b) - displayGain(a));
    } else if (sortMode === 'accept') {
      result.sort((a, b) => ((b.trade_package || b.trade || {}).acceptance_probability || 0) - ((a.trade_package || a.trade || {}).acceptance_probability || 0));
    } else if (sortMode === 'needs') {
      const needCount = (s) => {
        const t = s.trade_package || s.trade || {};
        return (t.fills_needs?.categories?.length || 0) + (t.fills_needs?.positions?.length || 0);
      };
      result.sort((a, b) => needCount(b) - needCount(a));
    }

    return result;
  }, [sequences, typeFilter, sortMode, catFilter]);

  // Empty state. DD-RO-CLASSAUDIT-0725 gate flip (honest empty state): if the output gate WITHHELD trades,
  // say so — a withheld rec is invisible by design, so the panel must not claim "none were generated".
  if (!tradeEngine || sequences.length === 0) {
    const withheld = tradeEngine?.gate?.withheld || 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: colors.neutral[400], fontSize: 13, textAlign: 'center', padding: '0 24px' }}>
        {withheld > 0
          ? `${withheld} trade${withheld === 1 ? '' : 's'} were withheld by a consistency check and not shown.`
          : 'No trade sequences generated'}
      </div>
    );
  }

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Section 1: Recommended Strategies ── */}
      {recommendations.length > 0 && (
        <div>
          <SectionLabel label="Recommended Strategies" right={`${recommendations.length} picks`} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {recommendations.map((rec, i) => (
              <RecCard key={i} rec={rec} teams={teams} />
            ))}
          </div>
        </div>
      )}

      {/* ── Section 2: Filter + Sort Bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '6px 10px', background: colors.neutral[900], borderRadius: 6,
        border: `1px solid ${colors.neutral[700]}`,
      }}>
        {/* Type filters */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[['all', 'All'], ['2team', '2-Team'], ['3team', '3-Team'], ['pick', 'W/ Pick']].map(([val, label]) => (
            <ToggleBtn key={val} label={label} active={typeFilter === val} onClick={() => setTypeFilter(val)} />
          ))}
        </div>

        <div style={{ width: 1, height: 18, background: colors.neutral[700] }} />

        {/* Sort */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[['pts', 'Pts Gain'], ['accept', 'Acceptance'], ['needs', 'Needs']].map(([val, label]) => (
            <ToggleBtn key={val} label={label} active={sortMode === val} onClick={() => setSortMode(val)} />
          ))}
        </div>

        {allCategories.length > 0 && (
          <>
            <div style={{ width: 1, height: 18, background: colors.neutral[700] }} />
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <ToggleBtn label="All" active={catFilter === 'all'} onClick={() => setCatFilter('all')} activeColor={colors.success} />
              {allCategories.map(c => {
                const hasSequences = activeCats.has(c);
                return (
                  <button
                    key={c}
                    onClick={() => hasSequences ? setCatFilter(c) : null}
                    style={{
                      padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      cursor: hasSequences ? 'pointer' : 'default',
                      border: `1px solid ${catFilter === c ? colors.success : colors.neutral[700]}`,
                      background: catFilter === c ? `${colors.success}20` : 'transparent',
                      color: catFilter === c ? colors.success : hasSequences ? colors.neutral[400] : colors.neutral[600],
                      opacity: hasSequences ? 1 : 0.4,
                      transition: 'all 0.15s',
                    }}
                  >{c}</button>
                );
              })}
            </div>
          </>
        )}

        {/* Count badge */}
        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: colors.neutral[500] }}>
          {filtered.length} sequence{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Section 3: Sequence Rows ── */}
      <div>
        <SectionLabel label="Trade Sequences" right={`${sequences.length} total`} />
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: colors.neutral[400], fontSize: 12 }}>
            No sequences match your filters
          </div>
        ) : (
          filtered.map((seq, i) => (
            <SequenceRow
              key={seq.sequence_id || i}
              seq={seq}
              rank={i + 1}
              leagueId={leagueId}
              teams={teams}
              onOpenDetail={setDetailSequence}
            />
          ))
        )}
      </div>
    </div>

    {detailSequence && (
      <OptimizerTradeDetail
        sequence={detailSequence}
        teams={teams}
        onClose={() => setDetailSequence(null)}
      />
    )}
    </>
  );
};

export default OptimizerTradeRecs;
